import React, { useEffect, useState, useRef, useMemo } from "react";
import { dms, inr } from "./api";
import { PageHeader } from "./OwnerPages";
import LocationDocumentsBlock from "./LocationDocumentsBlock";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Upload, Download, Plus, Trash2, FileText, Eye, Printer, Search } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

// tiny helper to trigger blob download
function downloadBlob(blob, name) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  document.body.removeChild(a); window.URL.revokeObjectURL(url);
}

// =====================================================================
// Import / Export Page (owner only)
// =====================================================================
export function ImportExportPage() {
  const [busy, setBusy] = useState("");
  const [importResult, setImportResult] = useState(null);
  const partiesRef = useRef(null);
  const productsRef = useRef(null);
  const saleBillsRef = useRef(null);
  const paymentsRef = useRef(null);

  const doExport = async (label, fn, filename) => {
    setBusy(label);
    try {
      const blob = await fn();
      downloadBlob(blob, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`${label} downloaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    setBusy("");
  };

  const doImport = async (label, fn, file) => {
    if (!file) return toast.error("Choose an .xlsx file");
    setBusy(label);
    try {
      const res = await fn(file);
      setImportResult({ label, ...res });
      toast.success(`${label} complete`);
    } catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    setBusy("");
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Import / Export" subtitle="Bulk data operations — Parties, Items, Sale Bills & Payments (import + export)" />

      <div className="grid md:grid-cols-2 gap-4">
        {/* Import Parties */}
        <Card className="p-5 border-emerald-200">
          <div className="flex items-center gap-3 mb-3">
            <Upload className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="font-semibold">Parties (Import / Export)</div>
              <div className="text-xs text-slate-500">Distributors + Retailers via multi-sheet XLSX</div>
            </div>
          </div>
          <input ref={partiesRef} type="file" accept=".xlsx" className="text-sm mb-3 block" data-testid="import-parties-file" />
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => doImport("Parties Import", dms.importParties, partiesRef.current?.files?.[0])} disabled={busy === "Parties Import"} className="bg-emerald-600 hover:bg-emerald-700">
              <Upload className="w-4 h-4 mr-1" />{busy === "Parties Import" ? "Uploading..." : "Import"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => doExport("Parties Export", dms.exportParties, "parties")} data-testid="export-parties-btn">
              <Download className="w-4 h-4 mr-1" />Export
            </Button>
          </div>
        </Card>

        {/* Import Items */}
        <Card className="p-5 border-emerald-200">
          <div className="flex items-center gap-3 mb-3">
            <Upload className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="font-semibold">Items / Products (Import / Export)</div>
              <div className="text-xs text-slate-500">Products with pricing via XLSX</div>
            </div>
          </div>
          <input ref={productsRef} type="file" accept=".xlsx" className="text-sm mb-3 block" data-testid="import-products-file" />
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => doImport("Items Import", dms.importProducts, productsRef.current?.files?.[0])} disabled={busy === "Items Import"} className="bg-emerald-600 hover:bg-emerald-700">
              <Upload className="w-4 h-4 mr-1" />{busy === "Items Import" ? "Uploading..." : "Import"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => doExport("Items Export", dms.exportProducts, "items")} data-testid="export-items-btn">
              <Download className="w-4 h-4 mr-1" />Export
            </Button>
          </div>
        </Card>

        {/* Sale Bills Import + Export */}
        <Card className="p-5 border-amber-200">
          <div className="flex items-center gap-3 mb-3">
            <FileText className="w-6 h-6 text-amber-600" />
            <div>
              <div className="font-semibold">Sale Bills (Import / Export)</div>
              <div className="text-xs text-slate-500">Retailer sale bills — posts to ledger on import</div>
            </div>
          </div>
          <input ref={saleBillsRef} type="file" accept=".xlsx" className="text-sm mb-3 block" data-testid="import-salebills-file" />
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => doImport("Sale Bills Import", dms.importSaleBills, saleBillsRef.current?.files?.[0])} disabled={busy === "Sale Bills Import"} className="bg-amber-600 hover:bg-amber-700">
              <Upload className="w-4 h-4 mr-1" />{busy === "Sale Bills Import" ? "Uploading..." : "Import"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => doExport("Sale Bills Template", dms.saleBillsTemplate, "sale_bills_template")} data-testid="salebills-template-btn">
              <Download className="w-4 h-4 mr-1" />Template
            </Button>
            <Button size="sm" variant="outline" onClick={() => doExport("Sale Bills Export", dms.exportSaleBills, "sale_bills")}>
              <Download className="w-4 h-4 mr-1" />Export
            </Button>
          </div>
        </Card>

        {/* Payments Import + Export */}
        <Card className="p-5 border-amber-200">
          <div className="flex items-center gap-3 mb-3">
            <FileText className="w-6 h-6 text-amber-600" />
            <div>
              <div className="font-semibold">Payments (Import / Export)</div>
              <div className="text-xs text-slate-500">Primary + Secondary payments</div>
            </div>
          </div>
          <input ref={paymentsRef} type="file" accept=".xlsx" className="text-sm mb-3 block" data-testid="import-payments-file" />
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => doImport("Payments Import", dms.importPayments, paymentsRef.current?.files?.[0])} disabled={busy === "Payments Import"} className="bg-amber-600 hover:bg-amber-700">
              <Upload className="w-4 h-4 mr-1" />{busy === "Payments Import" ? "Uploading..." : "Import"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => doExport("Payments Template", dms.paymentsTemplate, "payments_template")} data-testid="payments-template-btn">
              <Download className="w-4 h-4 mr-1" />Template
            </Button>
            <Button size="sm" variant="outline" onClick={() => doExport("Payments Export", dms.exportPayments, "payments")}>
              <Download className="w-4 h-4 mr-1" />Export
            </Button>
          </div>
        </Card>
      </div>

      {importResult && (
        <Card className="p-4 border-emerald-200 bg-emerald-50">
          <div className="font-semibold text-emerald-800 mb-2">{importResult.label} Result</div>
          <pre className="text-xs bg-white p-3 rounded overflow-auto">{JSON.stringify(importResult, null, 2)}</pre>
        </Card>
      )}
    </div>
  );
}

// =====================================================================
// Direct Sales Page (owner + distributor)
// =====================================================================
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

export function DirectSalesPage() {
  const { user } = useAuth();
  const isRetailer = user?.role === "retailer";
  const canBillDistributor = user?.role === "owner" || user?.role === "super_admin";
  const [billFor, setBillFor] = useState("retailer");
  const [distributors, setDistributors] = useState([]);
  const [allRetailers, setAllRetailers] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    distributor_id: "",
    retailer_id: "",
    date: today(),
    billing_name: "",
    eway_bill_no: "",
    notes: "",
    customer: { name: "", phone: "", address: "", gstin: "" },
    transport: { mode: "", vehicle_no: "", transporter: "", lr_no: "" },
    items: [{ product_id: "", qty_boxes: 1, box_price: 0 }],
  });
  const [busy, setBusy] = useState(false);
  const [lastBill, setLastBill] = useState(null);
  const [priceMap, setPriceMap] = useState({});
  const [history, setHistory] = useState([]);

  // Invoice numbering — editable only at creation time
  const [invoicePrefix, setInvoicePrefix] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");

  // Credit/Cash + payment tracking
  const [billType, setBillType] = useState("credit"); // "credit" | "cash"
  const [receivedChecked, setReceivedChecked] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [paymentType, setPaymentType] = useState("cash");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [stateOfSupply, setStateOfSupply] = useState("");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null); // { name, url(base64) }
  const [editingId, setEditingId] = useState(null); // bill id being edited, null = create mode

  const loadHistory = () => { dms.listDirectSales().then(r => setHistory(r.data || r || [])).catch(() => {}); };
  useEffect(() => { loadHistory(); }, []);

  const refreshInvoiceNumber = () => {
    dms.getNumberSettings().then(ns => {
      const s = (ns && ns.retailer_bill) || {};
      setInvoicePrefix(s.prefix || "INV");
      setInvoiceNumber(String(s.next_seq || 1).padStart(s.width || 4, "0"));
    }).catch(() => {});
  };
  useEffect(() => { refreshInvoiceNumber(); }, []);

  // Bank accounts for Payment Type — silently empty for roles without access
  useEffect(() => {
    dms.listBankAccounts().then(d => setBankAccounts(d.data || d || [])).catch(() => setBankAccounts([]));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const d = await dms.listDistributors();
        setDistributors(d.data || d || []);
        // preselect for distributor role
        if (user?.role === "distributor" || user?.role === "distributor_accountant") {
          setForm(f => ({ ...f, distributor_id: user.distributor_id || "" }));
        }
        const p = await dms.listProducts();
        setProducts(p.data || p || []);
        if (user?.role !== "distributor" && user?.role !== "distributor_accountant") {
          try {
            const ar = await dms.listRetailers({ lite: 1 });
            setAllRetailers(ar.data || ar || []);
          } catch (e) { setAllRetailers([]); }
        }
      } catch (e) { toast.error("Failed loading masters"); }
    })();
  }, [user]);

  // when distributor changes, load their retailers
  useEffect(() => {
    if (!form.distributor_id) { setRetailers([]); setPriceMap({}); return; }
    (async () => {
      try {
        const r = await dms.listRetailers({ distributor_id: form.distributor_id, lite: 1 });
        setRetailers(r.data || r || []);
      } catch (e) { setRetailers([]); }
      try {
        const pr = await dms.getRetailerPrices(form.distributor_id);
        const rows = pr.data || pr || [];
        const map = {};
        rows.forEach(row => { map[row.product_id] = row.selling_price; });
        setPriceMap(map);
      } catch (e) { setPriceMap({}); }
    })();
  }, [form.distributor_id]);

  const [openCombo, setOpenCombo] = useState(null);
  const [browseLineIdx, setBrowseLineIdx] = useState(null);
  const [qtyUnit, setQtyUnit] = useState("box"); // "box" or "pcs" — controls how quantity/price are entered & displayed
  const productsByCategory = useMemo(() => {
    const acc = {};
    for (const p of products) {
      const cat = p.category_name || "Uncategorized";
      (acc[cat] = acc[cat] || []).push(p);
    }
    return acc;
  }, [products]);

  const handleQtyUnitChange = (newUnit) => {
    if (newUnit === qtyUnit) return;
    setForm(f => ({
      ...f,
      items: f.items.map(it => {
        const p = products.find(x => x.id === it.product_id);
        const bq = Number(p?.box_qty || 1);
        if (newUnit === "pcs") {
          const pcs = Math.round(Number(it.qty_boxes || 0) * bq + Number(it.qty_pcs || 0));
          return { ...it, qty_boxes: 0, qty_pcs: pcs };
        } else {
          const boxes = Math.round(Number(it.qty_pcs || 0) / bq + Number(it.qty_boxes || 0));
          return { ...it, qty_boxes: boxes, qty_pcs: 0 };
        }
      })
    }));
    setQtyUnit(newUnit);
  };

  const addLine = () => setForm({ ...form, items: [...form.items, qtyUnit === "pcs" ? { product_id: "", qty_boxes: 0, qty_pcs: 1, box_price: 0 } : { product_id: "", qty_boxes: 1, qty_pcs: 0, box_price: 0 }] });
  const rmLine = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updLine = (i, patch) => {
    const items = [...form.items]; items[i] = { ...items[i], ...patch };
    // auto-fill box price whenever the product changes (always overwrite — a new
    // product selection should always replace the previous product's price)
    if (patch.product_id) {
      const p = products.find(x => x.id === patch.product_id);
      if (p) {
        const override = Number(priceMap[p.id] || 0);
        // Default (no retailer-specific price set): use the price-list's
        // GST-inclusive per-piece price x this product's own box quantity —
        // NOT a flat markup. Falls back to unit_price*box_qty if the
        // uploaded price list didn't have price_with_gst for this item.
        const perPieceWithGst = Number(p.price_with_gst || 0);
        const boxQty = Number(p.box_qty || 1);
        const listBoxPrice = perPieceWithGst > 0
          ? perPieceWithGst * boxQty
          : Number(p.unit_price || 0) * boxQty;
        items[i].box_price = override > 0
          ? Math.round(override * 100) / 100
          : Math.round(listBoxPrice * 100) / 100;
      }
    }
    setForm({ ...form, items });
  };

  const subtotal = form.items.reduce((s, it) => s + (Number(it.qty_boxes || 0) * Number(it.box_price || 0)), 0);
  const effectiveReceived = billType === "cash" ? subtotal : (receivedChecked ? Number(receivedAmount || 0) : 0);
  const balanceDue = Math.max(0, Math.round((subtotal - effectiveReceived) * 100) / 100);

  const onPickDocument = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setAttachment({ name: f.name, url: rd.result });
    rd.readAsDataURL(f);
  };

  const resetPaymentFields = () => {
    setBillType("credit"); setReceivedChecked(false); setReceivedAmount(0);
    setPaymentType("cash"); setBankAccountId(""); setStateOfSupply("");
    setDescription(""); setAttachment(null); setReferenceNo("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ distributor_id: form.distributor_id, retailer_id: "", date: today(), billing_name: "", eway_bill_no: "", notes: "", customer: { name: "", phone: "", address: "", gstin: "" }, transport: { mode: "", vehicle_no: "", transporter: "", lr_no: "" }, items: [{ product_id: "", qty_boxes: 1, box_price: 0 }] });
    resetPaymentFields();
    refreshInvoiceNumber();
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setForm({
      distributor_id: b.distributor_id || "",
      retailer_id: b.retailer_id || "",
      date: b.date || today(),
      billing_name: b.billing_name || "",
      eway_bill_no: b.eway_bill_no || "",
      notes: b.notes || "",
      customer: { name: b.customer?.name || "", phone: b.customer?.phone || "", address: b.customer?.address || "", gstin: b.customer?.gstin || "" },
      transport: { mode: b.transport?.mode || "", vehicle_no: b.transport?.vehicle_no || "", transporter: b.transport?.transporter || "", lr_no: b.transport?.lr_no || "" },
      items: (b.items || []).map(it => ({ product_id: it.product_id, qty_boxes: it.qty_boxes_dispatched ?? it.dispatched_qty_boxes ?? 0, box_price: it.box_price || 0 })),
    });
    const no = b.bill_no || "";
    const idx = no.lastIndexOf("-");
    setInvoicePrefix(idx === -1 ? "" : no.slice(0, idx));
    setInvoiceNumber(idx === -1 ? no : no.slice(idx + 1));
    setBillType(b.bill_type || "credit");
    setReceivedChecked(Number(b.received_amount || 0) > 0);
    setReceivedAmount(b.received_amount || 0);
    setPaymentType(b.bank_account_id || (b.payment_type === "cheque" ? "cheque" : b.payment_type) || "cash");
    setBankAccountId(b.bank_account_id || "");
    setReferenceNo(b.reference_no || "");
    setStateOfSupply(b.state_of_supply || "");
    setDescription(b.description || "");
    setAttachment(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    const billingDistributor = canBillDistributor && billFor === "distributor";
    if (!isRetailer) {
      if (billingDistributor) {
        if (!form.distributor_id) return toast.error("Choose distributor");
      } else {
        if (!form.retailer_id) return toast.error("Choose retailer");
      }
    } else {
      if (!form.customer.name) return toast.error("Enter customer name");
    }
    const items = form.items.filter(it => it.product_id && (Number(it.qty_boxes) > 0 || Number(it.qty_pcs) > 0));
    if (items.length === 0) return toast.error("Add at least one product line");
    if (billType === "credit" && receivedChecked && Number(receivedAmount) > subtotal) {
      return toast.error("Received amount cannot exceed total");
    }
    setBusy(true);
    try {
      const composedBillNo = (invoicePrefix ? `${invoicePrefix}-${invoiceNumber}` : invoiceNumber).trim();
      const common = {
        billing_name: form.billing_name,
        eway_bill_no: form.eway_bill_no,
        bill_type: billType,
        received_amount: effectiveReceived,
        payment_type: paymentType === "bank" ? "bank" : paymentType,
        bank_account_id: paymentType !== "cash" && paymentType !== "cheque" ? paymentType : "",
        state_of_supply: stateOfSupply,
        description,
        attachment: attachment || undefined,
        bill_no: composedBillNo || undefined,
        reference_no: paymentType !== "cash" ? referenceNo : "",
      };
      const payload = isRetailer
        ? { date: form.date, notes: form.notes, customer: form.customer, transport: form.transport, items, ...common }
        : billingDistributor
          ? { ...form, retailer_id: undefined, bill_for: "distributor", items, ...common }
          : { ...form, items, ...common };
      const bill = editingId
        ? await dms.fullEditDirectSale(editingId, payload)
        : await dms.createDirectSale(payload);
      setLastBill(bill);
      toast.success(editingId ? `Bill ${bill.bill_no} updated` : `Bill ${bill.bill_no} created`);
      loadHistory();
      refreshInvoiceNumber();
      setEditingId(null);
      setForm({ distributor_id: form.distributor_id, retailer_id: "", date: today(), billing_name: "", eway_bill_no: "", notes: "", customer: { name: "", phone: "", address: "", gstin: "" }, transport: { mode: "", vehicle_no: "", transporter: "", lr_no: "" }, items: [{ product_id: "", qty_boxes: 1, box_price: 0 }] });
      resetPaymentFields();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={editingId ? "Edit Tax Invoice" : "+Add Sales (Direct Invoice)"}
        subtitle={editingId ? "Editing an existing bill — stock will be re-adjusted on save" : (isRetailer ? "Create a bill for a walk-in customer" : "Create a retailer bill without a sales order")}
        action={editingId ? <Button variant="outline" onClick={cancelEdit}>Cancel Edit</Button> : undefined}
      />

      <Card className="p-5">
        {/* Header row — Invoice No / Date / Credit-Cash toggle */}
        <div className="grid md:grid-cols-4 gap-3 mb-4 items-end">
          <div><Label>Invoice Prefix</Label><Input value={invoicePrefix} onChange={e => setInvoicePrefix(e.target.value.toUpperCase())} placeholder="INV" data-testid="ds-invoice-prefix" /></div>
          <div><Label>Invoice No.*</Label><Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="0001" data-testid="ds-invoice-no" /></div>
          <div><Label>Date*</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setBillType("credit")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${billType === "credit" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}
              data-testid="ds-type-credit">Credit</button>
            <button type="button" onClick={() => setBillType("cash")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${billType === "cash" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}
              data-testid="ds-type-cash">Cash</button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-4">
          {isRetailer ? (
            <>
              <div><Label>Customer Name*</Label><Input value={form.customer.name} onChange={e => setForm({ ...form, customer: { ...form.customer, name: e.target.value } })} placeholder="Walk-in customer" data-testid="ds-customer-name" /></div>
              <div><Label>Customer Phone</Label><Input value={form.customer.phone} onChange={e => setForm({ ...form, customer: { ...form.customer, phone: e.target.value } })} /></div>
              <div><Label>Customer GSTIN</Label><Input value={form.customer.gstin} onChange={e => setForm({ ...form, customer: { ...form.customer, gstin: e.target.value } })} /></div>
              <div className="md:col-span-3"><Label>Customer Address</Label><Input value={form.customer.address} onChange={e => setForm({ ...form, customer: { ...form.customer, address: e.target.value } })} /></div>
            </>
          ) : (
            <>
              {canBillDistributor && (
                <div className="md:col-span-3">
                  <Label>Bill For*</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => { setBillFor("retailer"); }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition ${billFor === "retailer" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}
                      data-testid="ds-billfor-retailer">Retailer</button>
                    <button type="button" onClick={() => { setBillFor("distributor"); setForm({ ...form, retailer_id: "" }); }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition ${billFor === "distributor" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}
                      data-testid="ds-billfor-distributor">Distributor</button>
                  </div>
                </div>
              )}
              {canBillDistributor && billFor === "distributor" ? (
                <div><Label>Distributor*</Label>
                  <Select value={form.distributor_id} onValueChange={v => setForm({ ...form, distributor_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Choose distributor" /></SelectTrigger>
                    <SelectContent>{distributors.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              ) : (
                <div><Label>Retailer*</Label>
                  <Select value={form.retailer_id} onValueChange={v => {
                    const isLockedDistributor = user?.role === "distributor" || user?.role === "distributor_accountant";
                    const pool = isLockedDistributor ? retailers : allRetailers;
                    const ret = pool.find(r => r.id === v);
                    setForm({ ...form, retailer_id: v, distributor_id: isLockedDistributor ? form.distributor_id : (ret?.distributor_id || "") });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Choose retailer" /></SelectTrigger>
                    <SelectContent>
                      {(user?.role === "distributor" || user?.role === "distributor_accountant" ? retailers : allRetailers)
                        .map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              )}
            </>
          )}
          <div><Label>Billing Name (optional)</Label><Input value={form.billing_name} onChange={e => setForm({ ...form, billing_name: e.target.value })} placeholder="Defaults to party name" data-testid="ds-billing-name" /></div>
          <div><Label>E-Way Bill No.</Label><Input value={form.eway_bill_no} onChange={e => setForm({ ...form, eway_bill_no: e.target.value })} data-testid="ds-eway-bill" /></div>
        </div>

        <div className="border rounded p-3 bg-slate-50">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium text-sm">Items</div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-md overflow-hidden border">
                <button type="button" onClick={() => handleQtyUnitChange("box")} className={`px-2 py-1 text-xs ${qtyUnit === "box" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`}>Box</button>
                <button type="button" onClick={() => handleQtyUnitChange("pcs")} className={`px-2 py-1 text-xs ${qtyUnit === "pcs" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`}>Pcs</button>
              </div>
              <Button size="sm" variant="outline" onClick={addLine}><Plus className="w-3 h-3 mr-1" />Add Line</Button>
            </div>
          </div>
          {form.items.map((it, i) => {
            const lineProduct = products.find(x => x.id === it.product_id);
            const boxQty = Number(lineProduct?.box_qty || 1);
            const pcsPrice = boxQty ? Number(it.box_price || 0) / boxQty : 0;
            const lineTotal = Number(it.qty_boxes || 0) * Number(it.box_price || 0) + Number(it.qty_pcs || 0) * pcsPrice;
            return (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
              <div className="col-span-6">
                <Popover open={openCombo === i} onOpenChange={(o) => setOpenCombo(o ? i : null)}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal">
                      <span className="truncate">
                        {(() => { const sp = products.find(p => p.id === it.product_id); return sp ? `${sp.name} (${sp.sku_code})` : "Choose product"; })()}
                      </span>
                      <Search className="w-3 h-3 ml-2 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search product or SKU..." />
                      <CommandList>
                        <CommandEmpty>
                          <div className="py-2 px-3 text-sm text-slate-500">No product found.</div>
                        </CommandEmpty>
                        {Object.entries(productsByCategory).map(([cat, items]) => (
                          <CommandGroup key={cat} heading={cat}>
                            {items.map(p => (
                              <CommandItem key={p.id} value={`${p.name} ${p.sku_code}`} onSelect={() => { updLine(i, { product_id: p.id }); setOpenCombo(null); }}>
                                {p.name} ({p.sku_code})
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        ))}
                      </CommandList>
                    </Command>
                    <div className="border-t p-2">
                      <Button size="sm" variant="ghost" className="w-full" onClick={() => { setOpenCombo(null); setBrowseLineIdx(i); }}>
                        Browse Products
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  min={0}
                  placeholder={qtyUnit === "pcs" ? "Pcs" : "Boxes"}
                  value={qtyUnit === "pcs" ? (it.qty_pcs || 0) : (it.qty_boxes || 0)}
                  onChange={e => {
                    const val = Number(e.target.value);
                    updLine(i, qtyUnit === "pcs" ? { qty_pcs: val, qty_boxes: 0 } : { qty_boxes: val, qty_pcs: 0 });
                  }}
                />
              </div>
              <div className="col-span-2 text-sm">
                <div className="text-slate-500 text-xs">
                  {qtyUnit === "pcs"
                    ? `Price/Pcs: ${inr(pcsPrice)}`
                    : `Price/Box: ${inr(Number(it.box_price || 0))}`}
                </div>
                <div className="font-semibold">{inr(lineTotal)}</div>
              </div>
              <div className="col-span-1"><Button size="sm" variant="outline" className="text-rose-600" onClick={() => rmLine(i)}><Trash2 className="w-3 h-3" /></Button></div>
            </div>
            );
          })}
        </div>

        <Dialog open={browseLineIdx !== null} onOpenChange={(o) => !o && setBrowseLineIdx(null)}>
          <DialogContent className="max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Browse Products</DialogTitle></DialogHeader>
            <Accordion type="multiple" defaultValue={Object.keys(productsByCategory)} className="w-full">
              {Object.entries(productsByCategory).map(([cat, items]) => (
                <AccordionItem key={cat} value={cat}>
                  <AccordionTrigger>{cat} ({items.length})</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 gap-1">
                      {items.map(p => (
                        <Button key={p.id} variant="ghost" className="justify-start" onClick={() => { updLine(browseLineIdx, { product_id: p.id }); setBrowseLineIdx(null); }}>
                          {p.name} ({p.sku_code})
                        </Button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </DialogContent>
        </Dialog>

        <div className="border rounded p-3 bg-slate-50 mt-4">
          <div className="font-medium text-sm mb-2">Transportation Details (optional)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div><Label>Mode</Label><Input value={form.transport.mode} onChange={e => setForm({ ...form, transport: { ...form.transport, mode: e.target.value } })} placeholder="Road / Rail" data-testid="ds-transport-mode" /></div>
            <div><Label>Vehicle No.</Label><Input value={form.transport.vehicle_no} onChange={e => setForm({ ...form, transport: { ...form.transport, vehicle_no: e.target.value } })} placeholder="DL01AB1234" /></div>
            <div><Label>Transporter</Label><Input value={form.transport.transporter} onChange={e => setForm({ ...form, transport: { ...form.transport, transporter: e.target.value } })} /></div>
            <div><Label>LR / Docket No.</Label><Input value={form.transport.lr_no} onChange={e => setForm({ ...form, transport: { ...form.transport, lr_no: e.target.value } })} /></div>
          </div>
        </div>

        <div className="border rounded p-3 bg-slate-50 mt-4">
          <div className="font-medium text-sm mb-2">Payment</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label>Payment Type</Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  {bankAccounts.map(b => <SelectItem key={b.id} value={b.id}>{b.name || b.account_name || "Bank A/c"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {paymentType !== "cash" && (
              <div>
                <Label>{paymentType === "cheque" ? "Cheque No." : "Transaction/Ref No."}</Label>
                <Input value={referenceNo} onChange={e => setReferenceNo(e.target.value)}
                  placeholder={paymentType === "cheque" ? "Cheque number" : "UTR / Ref number"}
                  data-testid="ds-reference-no" />
              </div>
            )}
            <div>
              <Label className="flex items-center gap-2">
                <input type="checkbox" className="h-3.5 w-3.5" checked={billType === "cash" ? true : receivedChecked}
                  disabled={billType === "cash"}
                  onChange={e => setReceivedChecked(e.target.checked)} />
                Received
              </Label>
              <Input type="number" min={0} value={billType === "cash" ? subtotal : receivedAmount}
                disabled={billType === "cash" || !receivedChecked}
                onChange={e => setReceivedAmount(Number(e.target.value))} data-testid="ds-received-amount" />
            </div>
            <div>
              <Label>Balance Due</Label>
              <div className="h-10 flex items-center font-semibold text-emerald-700" data-testid="ds-balance-due">{inr(balanceDue)}</div>
            </div>
            <div>
              <Label>State of Supply</Label>
              <Select value={stateOfSupply} onValueChange={setStateOfSupply}>
                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          <div><Label>Description</Label><Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} /></div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-3 items-start">
          <div>
            <Label>Add Document (optional)</Label>
            <input type="file" accept="image/*,.pdf" onChange={onPickDocument} className="block text-sm mt-1" data-testid="ds-attach-doc" />
            {attachment && <div className="text-xs text-slate-500 mt-1">Attached: {attachment.name} <button className="text-rose-600 ml-2" onClick={() => setAttachment(null)}>Remove</button></div>}
          </div>
          <div className="flex flex-col items-end justify-end">
            <div className="text-sm text-slate-500">Subtotal</div>
            <div className="text-2xl font-bold text-amber-700">{inr(subtotal)}</div>
            <Button className="mt-3 bg-amber-600 hover:bg-amber-700" onClick={submit} disabled={busy}>
              {busy ? (editingId ? "Updating..." : "Creating...") : (editingId ? "Update Bill" : "Create Bill")}
            </Button>
          </div>
        </div>
      </Card>

      {lastBill && (
        <Card className="p-4 border-emerald-200 bg-emerald-50">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-emerald-800">Last Created: {lastBill.bill_no}</div>
              <div className="text-sm text-slate-700">Total: {inr(lastBill.total)} · Items: {lastBill.items?.length || 0} · Balance Due: {inr(lastBill.balance_due || 0)}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => window.open(`/dms/print/retailer-bill/${lastBill.id}`, "_blank")} data-testid="ds-print-bill">
                Print / View Invoice
              </Button>
              {!isRetailer && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={async () => {
                    if (!window.confirm(`Delete bill ${lastBill.bill_no}? This will reverse stock and cannot be undone.`)) return;
                    try {
                      await dms.deleteDirectSale(lastBill.id);
                      toast.success(`Bill ${lastBill.bill_no} deleted`);
                      setLastBill(null);
                      loadHistory();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Delete failed");
                    }
                  }}
                  data-testid="ds-delete-bill"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="font-medium text-sm mb-3">Direct Sale History</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b">
                <th className="py-2 pr-3">Bill No.</th>
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Items</th>
                <th className="py-2 pr-3 text-right">Total</th>
                <th className="py-2 pr-3 text-right">Balance Due</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map(b => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium">{b.bill_no}</td>
                  <td className="py-2 pr-3">{b.date}</td>
                  <td className="py-2 pr-3">{b.items?.length || 0}</td>
                  <td className="py-2 pr-3 text-right">{inr(b.total)}</td>
                  <td className="py-2 pr-3 text-right">{inr(b.balance_due || 0)}</td>
                  <td className="py-2 pr-3 text-right">
                    <div className="flex gap-2 justify-end">
                      {!isRetailer && (
                        <Button size="sm" variant="outline" onClick={() => startEdit(b)} data-testid={`edit-direct-sale-${b.id}`}>
                          Edit
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => window.open(`/dms/print/retailer-bill/${b.id}`, "_blank")}>
                        Print
                      </Button>
                      {!isRetailer && (
                        <Button
                          size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50"
                          onClick={async () => {
                            if (!window.confirm(`Delete bill ${b.bill_no}? This will reverse stock and cannot be undone.`)) return;
                            try {
                              await dms.deleteDirectSale(b.id);
                              toast.success(`Bill ${b.bill_no} deleted`);
                              if (lastBill?.id === b.id) setLastBill(null);
                              loadHistory();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Delete failed");
                            }
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-400">No direct-sale bills yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// =====================================================================
// Documents Page (owner + distributor) — Estimate, DC, SR, CN, DN
// =====================================================================
const DOC_TYPES = [
  { value: "estimate", label: "Estimate / Quotation", prefix: "EST" },
  { value: "delivery_challan", label: "Delivery Challan", prefix: "DC" },
  { value: "sale_return", label: "Sale Return", prefix: "SR" },
  { value: "credit_note", label: "Credit Note", prefix: "CN" },
  { value: "debit_note", label: "Debit Note", prefix: "DN" },
];
// Delivery Challan is auto-generated from the Dispatch flow — not creatable here.
const DOC_CREATE_TYPES = DOC_TYPES.filter(t => t.value !== "delivery_challan");

export function DocumentsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ type: "", start: "", end: "" });
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [distributors, setDistributors] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    type: "estimate", date: today(),
    party_type: "retailer", party_id: "",
    items: [{ description: "", product_id: "", qty: 1, unit: "box", rate: 0 }],
    gst_pct: 0, notes: "",
  });

  const load = async () => {
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const d = await dms.listDocuments(params); setRows(d.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    dms.listDistributors().then(d => setDistributors(d.data || d || []));
    dms.listRetailers({ lite: 1 }).then(r => setRetailers(r.data || r || []));
    dms.listProducts().then(p => setProducts(p.data || p || []));
  }, []);

  const addLine = () => setForm({ ...form, items: [...form.items, { description: "", product_id: "", qty: 1, unit: "box", rate: 0 }] });
  const rmLine = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updLine = (i, patch) => {
    const items = [...form.items]; items[i] = { ...items[i], ...patch };
    if (patch.product_id) {
      const p = products.find(x => x.id === patch.product_id);
      if (p) { items[i].description = p.name; if (!items[i].rate) items[i].rate = p.unit_price || 0; }
    }
    setForm({ ...form, items });
  };

  const subtotal = form.items.reduce((s, it) => s + (Number(it.qty) * Number(it.rate || 0)), 0);
  const gst_total = subtotal * Number(form.gst_pct || 0) / 100;
  const total = subtotal + gst_total;

  const submit = async () => {
    if (!form.party_id) return toast.error("Choose party");
    if (form.type === "sale_return" && (!(form.ref_invoice_no || "").trim() || !form.ref_invoice_date)) return toast.error("Enter original invoice number and date");
    const items = form.items.filter(it => it.description && Number(it.qty) > 0);
    if (items.length === 0) return toast.error("Add at least one item");
    try {
      if (editingId) { await dms.updateDocument(editingId, { ...form, items }); toast.success("Document updated"); }
      else { await dms.createDocument({ ...form, items }); toast.success("Document created"); }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openView = async (r) => {
    try { const d = await dms.getDocument(r.id); setViewDoc(d); setViewOpen(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = async (r) => {
    try {
      const d = await dms.getDocument(r.id);
      setForm({ type: d.type, date: d.date, party_type: d.party_type, party_id: d.party_id,
        items: (d.items || []).map(it => ({ description: it.description || "", product_id: it.product_id || "", qty: it.qty, unit: it.unit || "box", rate: it.rate })),
        gst_pct: d.gst_pct || 0, notes: d.notes || "", ref_invoice_no: d.ref_invoice_no || "", ref_invoice_date: d.ref_invoice_date || "" });
      setEditingId(d.id); setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeDoc = async (r) => {
    if (!window.confirm("Delete " + r.doc_no + "? This cannot be undone.")) return;
    try { await dms.deleteDocument(r.id); toast.success("Document deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const parties = form.party_type === "retailer" ? retailers : distributors;
  const label = (v) => DOC_TYPES.find(t => t.value === v)?.label || v;
  const badgeColor = { estimate: "bg-sky-600", delivery_challan: "bg-emerald-600", sale_return: "bg-rose-600", credit_note: "bg-violet-600", debit_note: "bg-amber-600" };

  return (
    <div className="space-y-4">
      <PageHeader title="Documents" subtitle="Estimate • Sale Return (updates stock) • Credit / Debit Note (posts to ledger)"
        action={<Button onClick={() => { setForm({ type: "estimate", date: today(), party_type: "retailer", party_id: "", items: [{ description: "", product_id: "", qty: 1, unit: "box", rate: 0 }], gst_pct: 0, notes: "" }); setEditingId(null); setOpen(true); }} className="bg-amber-600 hover:bg-amber-700"><Plus className="w-4 h-4 mr-2" />New Document</Button>} />

      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-end">
          <div><Label className="text-xs">Type</Label>
            <Select value={filter.type || "all"} onValueChange={v => setFilter({ ...filter, type: v === "all" ? "" : v })}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>{DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">From</Label><Input type="date" value={filter.start} onChange={e => setFilter({ ...filter, start: e.target.value })} /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={filter.end} onChange={e => setFilter({ ...filter, end: e.target.value })} /></div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Doc No.</TableHead><TableHead>Type</TableHead><TableHead>Date</TableHead><TableHead>Party</TableHead><TableHead className="text-right">Total</TableHead><TableHead>By</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No documents yet.</TableCell></TableRow>
              : rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{r.doc_no}</TableCell>
                  <TableCell><Badge className={badgeColor[r.type] || "bg-slate-500"}>{label(r.type)}</Badge></TableCell>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.party_name}</TableCell>
                  <TableCell className="text-right font-medium">{inr(r.total)}</TableCell>
                  <TableCell className="text-xs">{r.created_by_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openView(r)}><Eye className="w-3 h-3 mr-1" />View</Button>
                    <Button size="sm" variant="outline" className="ml-1" onClick={() => window.open(`/dms/print/document/${r.id}`, "_blank")}><Printer className="w-3 h-3" /></Button>
                    {r.type !== "delivery_challan" && <Button size="sm" variant="outline" className="ml-1" onClick={() => startEdit(r)}>Edit</Button>}
                    {r.type !== "delivery_challan" && <Button size="sm" variant="outline" className="ml-1 text-rose-600" onClick={() => removeDoc(r)}><Trash2 className="w-3 h-3" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit Document" : "New Document"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[75vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type*</Label>
                <Select disabled={!!editingId} value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_CREATE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Date*</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>Party Type*</Label>
                <Select value={form.party_type} onValueChange={v => setForm({ ...form, party_type: v, party_id: "" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="retailer">Retailer</SelectItem><SelectItem value="distributor">Distributor</SelectItem></SelectContent>
                </Select></div>
              <div><Label>Party*</Label>
                <Select value={form.party_id} onValueChange={v => setForm({ ...form, party_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>{parties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            {form.type === "sale_return" && (
              <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-2.5">
                <b>Stock effect:</b> Return from a <b>Retailer</b> → your stock <b>increases</b>. Return to <b>Company</b> (party = your distributor) → your stock <b>decreases</b>. Pick the product and choose Box or Pcs for each item; pieces are converted to boxes for stock.
              </div>
            )}
            {form.type === "sale_return" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Original Invoice No.*</Label><Input placeholder="Invoice the goods were sold on" value={form.ref_invoice_no || ""} onChange={e => setForm({ ...form, ref_invoice_no: e.target.value })} /></div>
                <div><Label>Invoice Date*</Label><Input type="date" value={form.ref_invoice_date || ""} onChange={e => setForm({ ...form, ref_invoice_date: e.target.value })} /></div>
              </div>
            )}
            {(form.type === "credit_note" || form.type === "debit_note") && (
              <div className="text-xs text-slate-600 bg-violet-50 border border-violet-200 rounded p-2.5">
                This {form.type === "credit_note" ? "Credit Note" : "Debit Note"} will be posted automatically to the selected party&apos;s ledger.
              </div>
            )}

            <div className="border rounded p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-sm">Items</div>
                <Button size="sm" variant="outline" onClick={addLine}><Plus className="w-3 h-3 mr-1" />Add Line</Button>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                  <div className="col-span-3">
                    <Select value={it.product_id || "custom"} onValueChange={v => { const pr = products.find(x => x.id === v); const bq = Number(pr && pr.box_qty) || 1; const base = Number(pr && pr.unit_price) || 0; updLine(i, { product_id: v === "custom" ? "" : v, ...(pr && !Number(it.rate) ? { rate: (it.unit === "pcs" ? base : Math.round(base * bq * 100) / 100) } : {}) }); }}>
                      <SelectTrigger><SelectValue placeholder="Product (or custom)" /></SelectTrigger>
                      <SelectContent><SelectItem value="custom">— Custom —</SelectItem>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3"><Input placeholder="Description" value={it.description} onChange={e => updLine(i, { description: e.target.value })} /></div>
                  <div className="col-span-1"><Input type="number" min={0} placeholder="Qty" value={it.qty} onChange={e => updLine(i, { qty: Number(e.target.value) })} /></div>
                  <div className="col-span-2">
                    <Select value={it.unit || "box"} onValueChange={u => { const pr = products.find(x => x.id === it.product_id); const bq = Number(pr && pr.box_qty) || 1; let r = Number(it.rate) || 0; if (pr && r > 0 && u !== (it.unit || "box")) { r = u === "pcs" ? r / bq : r * bq; r = Math.round(r * 100) / 100; } updLine(i, { unit: u, rate: r }); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="box">Box</SelectItem><SelectItem value="pcs">Pcs</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input type="number" min={0} placeholder="Rate" value={it.rate} onChange={e => updLine(i, { rate: Number(e.target.value) })} /></div>
                  <div className="col-span-1"><Button size="sm" variant="outline" className="text-rose-600" onClick={() => rmLine(i)}><Trash2 className="w-3 h-3" /></Button></div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><Label>GST %</Label><Input type="number" min={0} value={form.gst_pct} onChange={e => setForm({ ...form, gst_pct: Number(e.target.value) })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>

            <div className="flex justify-end gap-4 text-sm border-t pt-3">
              <div><span className="text-slate-500">Subtotal: </span>{inr(subtotal)}</div>
              <div><span className="text-slate-500">GST: </span>{inr(gst_total)}</div>
              <div className="font-semibold text-lg text-amber-700"><span className="text-slate-500 text-sm">Total: </span>{inr(total)}</div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-amber-600 hover:bg-amber-700" onClick={submit}>{editingId ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{viewDoc && label(viewDoc.type)} — {viewDoc?.doc_no}</DialogTitle></DialogHeader>
          {viewDoc && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-slate-500">Date: </span>{viewDoc.date}</div>
                <div><span className="text-slate-500">Party: </span>{viewDoc.party_name}</div>
                <div><span className="text-slate-500">By: </span>{viewDoc.created_by_name || "—"}</div>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Description</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>{(viewDoc.items || []).map((it, i) => (
                  <TableRow key={i}><TableCell>{it.description}</TableCell><TableCell className="text-right">{it.qty}{it.unit ? " " + it.unit : ""}</TableCell><TableCell className="text-right">{inr(it.rate)}</TableCell><TableCell className="text-right">{inr(it.amount)}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
              <div className="flex justify-end gap-4 text-sm border-t pt-3">
                <div><span className="text-slate-500">Subtotal: </span>{inr(viewDoc.subtotal)}</div>
                <div><span className="text-slate-500">GST: </span>{inr(viewDoc.gst_total)}</div>
                <div className="font-semibold text-lg text-amber-700">{inr(viewDoc.total)}</div>
              </div>
              {viewDoc.notes && <div className="text-sm text-slate-600"><strong>Notes:</strong> {viewDoc.notes}</div>}
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => window.open(`/dms/print/document/${viewDoc.id}`, "_blank")}><Printer className="w-3 h-3 mr-1" />Print</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =====================================================================
// Document Print View — standalone printable page (with distinct per-type styling)
// =====================================================================
const DOC_STYLES = {
  estimate: {
    accent: "#3b82f6",           // blue-500
    bg: "bg-blue-50",
    border: "border-blue-500",
    text: "text-blue-700",
    tag: "PROPOSAL",
  },
  delivery_challan: {
    accent: "#059669",           // emerald-600
    bg: "bg-emerald-50",
    border: "border-emerald-600",
    text: "text-emerald-700",
    tag: "DISPATCH",
  },
  sale_return: {
    accent: "#e11d48",           // rose-600
    bg: "bg-rose-50",
    border: "border-rose-600",
    text: "text-rose-700",
    tag: "RETURN",
  },
  credit_note: {
    accent: "#7c3aed",           // violet-600
    bg: "bg-violet-50",
    border: "border-violet-600",
    text: "text-violet-700",
    tag: "CR NOTE",
  },
  debit_note: {
    accent: "#ea580c",           // orange-600
    bg: "bg-orange-50",
    border: "border-orange-600",
    text: "text-orange-700",
    tag: "DR NOTE",
  },
};
const DEFAULT_DOC_STYLE = {
  accent: "#a67c00",
  bg: "bg-amber-50",
  border: "border-amber-600",
  text: "text-amber-700",
  tag: "DOCUMENT",
};

export function DocumentPrintPage() {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    const id = window.location.pathname.split("/").pop();
    dms.printDocument(id).then(d => setDoc(d)).catch(() => toast.error("Failed"));
  }, []);
  if (!doc) return <div className="p-6 text-slate-500">Loading…</div>;

  const style = DOC_STYLES[doc.type] || DEFAULT_DOC_STYLE;

  return (
    <div className="p-8 max-w-3xl mx-auto bg-white">
      {/* Header — distinct per doc type */}
      <div className={`mb-6 pb-4 border-b-2 ${style.border}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className={`text-3xl font-bold ${style.text}`}>{doc.company_name}</h1>
            <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-widest ${style.bg} ${style.text} border ${style.border}`}>
              <span>{style.tag}</span>
              <span className="opacity-40">•</span>
              <span>{doc.doc_type_label}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase text-slate-500 tracking-wide">{doc.doc_type_label} No</div>
            <div className={`text-lg font-mono font-semibold ${style.text}`}>{doc.doc_no}</div>
            <div className="text-xs text-slate-500 mt-1">Date: {doc.date}</div>
          </div>
        </div>
      </div>

      {/* Party block */}
      <div className={`p-4 rounded-lg ${style.bg} border ${style.border} mb-5`}>
        <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Party</div>
        <div className="font-semibold text-slate-800">{doc.party_name}</div>
        <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-600">
          {doc.party?.gstin && <div><strong>GSTIN:</strong> {doc.party.gstin}</div>}
          {doc.party?.phone && <div><strong>Phone:</strong> {doc.party.phone}</div>}
          {doc.party?.address && <div className="col-span-2"><strong>Address:</strong> {doc.party.address}</div>}
        </div>
      </div>

      {/* Items */}
      <table className="w-full border-collapse mb-4 text-sm">
        <thead className={style.bg}><tr>
          <th className="border p-2 text-left">Description</th>
          <th className="border p-2 text-right">Qty</th>
          <th className="border p-2 text-right">Rate</th>
          <th className="border p-2 text-right">Amount</th>
        </tr></thead>
        <tbody>{(doc.items || []).map((it, i) => (
          <tr key={i}>
            <td className="border p-2">{it.description}</td>
            <td className="border p-2 text-right">{it.qty}</td>
            <td className="border p-2 text-right">{inr(it.rate)}</td>
            <td className="border p-2 text-right">{inr(it.amount)}</td>
          </tr>
        ))}</tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end mb-6"><div className="w-64 text-sm space-y-1">
        <div className="flex justify-between"><span>Subtotal</span><span>{inr(doc.subtotal)}</span></div>
        <div className="flex justify-between"><span>GST ({doc.gst_pct}%)</span><span>{inr(doc.gst_total)}</span></div>
        <div className={`flex justify-between font-bold text-lg border-t pt-1`}>
          <span>Total</span><span className={style.text}>{inr(doc.total)}</span>
        </div>
      </div></div>

      {doc.invoice_message && <div className="text-center italic text-slate-600 mb-4">{doc.invoice_message}</div>}
      {doc.invoice_terms && <div className="text-xs text-slate-500 border-t pt-3 whitespace-pre-wrap">{doc.invoice_terms}</div>}
      <div className="mt-6 text-center print:hidden">
        <Button onClick={() => window.print()} style={{ backgroundColor: style.accent }}>
          <Printer className="w-4 h-4 mr-2" />Print
        </Button>
      </div>
    </div>
  );
}

// =====================================================================
// My Bank / Payment Details — self-service for distributor & retailer
// =====================================================================
export function MyBankPage() {
  const [bank, setBank] = useState({ bank_name: "", bank_account: "", bank_ifsc: "", bank_branch: "", upi_id: "", upi_name: "", qr_url: "", gstin: "" });
  const [meta, setMeta] = useState({ name: "", party_type: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await dms.getMyBank();
        setMeta({ name: r.name, party_type: r.party_type });
        setBank({ bank_name: "", bank_account: "", bank_ifsc: "", bank_branch: "", upi_id: "", upi_name: "", qr_url: "", gstin: "", ...(r.bank || {}) });
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await dms.updateMyBank(bank);
      toast.success("Payment details saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4">
      <PageHeader title="My Payment Details" subtitle="Your bank account + UPI QR — shown on invoices and visible to the owner" />
      <Card className="p-6 max-w-3xl">
        <div className="text-sm text-slate-500 mb-3">Profile: <span className="font-semibold text-slate-800">{meta.name}</span></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>GSTIN</Label><Input value={bank.gstin} onChange={e => setBank({ ...bank, gstin: e.target.value })} data-testid="mybank-gstin" /></div>
          <div><Label>Bank Name</Label><Input value={bank.bank_name} onChange={e => setBank({ ...bank, bank_name: e.target.value })} data-testid="mybank-name" /></div>
          <div><Label>Account No.</Label><Input value={bank.bank_account} onChange={e => setBank({ ...bank, bank_account: e.target.value })} data-testid="mybank-account" /></div>
          <div><Label>IFSC</Label><Input value={bank.bank_ifsc} onChange={e => setBank({ ...bank, bank_ifsc: e.target.value })} /></div>
          <div><Label>Branch</Label><Input value={bank.bank_branch} onChange={e => setBank({ ...bank, bank_branch: e.target.value })} /></div>
          <div><Label>UPI ID</Label><Input value={bank.upi_id} onChange={e => setBank({ ...bank, upi_id: e.target.value })} placeholder="name@bank" data-testid="mybank-upi" /></div>
          <div><Label>UPI Payee Name</Label><Input value={bank.upi_name} onChange={e => setBank({ ...bank, upi_name: e.target.value })} /></div>
          <div>
            <Label>Payment QR (image)</Label>
            <div className="flex items-center gap-2 mt-1">
              {bank.qr_url ? <img src={bank.qr_url} alt="qr" className="h-14 w-14 object-contain border rounded" /> : <div className="h-14 w-14 border rounded flex items-center justify-center text-[10px] text-slate-400">No QR</div>}
              <input type="file" accept="image/*" data-testid="mybank-qr" onChange={(e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const rd = new FileReader(); rd.onload = () => setBank(v => ({ ...v, qr_url: rd.result })); rd.readAsDataURL(f);
              }} className="text-xs" />
              {bank.qr_url && <Button variant="outline" size="sm" onClick={() => setBank(v => ({ ...v, qr_url: "" }))}>Remove</Button>}
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="mybank-save">
            {saving ? "Saving…" : "Save Payment Details"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------
// My Profile: distributor edits own details (name locked). Changes show in Owner > Activity Log.
// ---------------------------------------------------------------------
export function MyProfilePage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setForm(await dms.getMyProfile()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { name, email, region, credit_limit, ...rest } = form;
      const r = await dms.updateMyProfile(rest);
      toast.success(r.changed ? "Profile updated" : "Nothing changed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  if (!form) return <div className="p-8 text-center text-sm text-slate-500">Loading…</div>;
  const set = (k, fn) => (e) => setForm({ ...form, [k]: fn ? fn(e.target.value) : e.target.value });
  const locked = "bg-slate-100 cursor-not-allowed";

  return (
    <div className="space-y-4">
      <PageHeader title="My Profile" subtitle="Your business details. Shown on your invoices and reports; the owner sees every change you make." />
      <Card className="p-6 max-w-4xl">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Business Name</Label>
            <Input value={form.name || ""} disabled className={locked} data-testid="myprofile-name" />
            <div className="text-[11px] text-slate-500 mt-1">Name cannot be changed</div>
          </div>
          <div><Label>Login Email</Label><Input value={form.email || ""} disabled className={locked} /></div>
          <div><Label>Region</Label><Input value={form.region || ""} disabled className={locked} /></div>
          <div><Label>Credit Limit</Label><Input value={inr(form.credit_limit)} disabled className={locked} /></div>

          <div><Label>Phone</Label><Input value={form.phone || ""} onChange={set("phone")} data-testid="myprofile-phone" /></div>
          <div><Label>GSTIN</Label><Input value={form.gstin || ""} maxLength={15} onChange={set("gstin", v => v.toUpperCase())} data-testid="myprofile-gstin" /></div>
          <div className="md:col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={set("address")} /></div>
          <div><Label>PAN</Label><Input value={form.pan || ""} maxLength={10} onChange={set("pan", v => v.toUpperCase())} /></div>
          <div><Label>Shop License</Label><Input value={form.shop_license || ""} onChange={set("shop_license")} /></div>

          <div><Label>Bank Name</Label><Input value={form.bank_name || ""} onChange={set("bank_name")} /></div>
          <div><Label>Account No.</Label><Input value={form.bank_account || ""} onChange={set("bank_account")} /></div>
          <div><Label>IFSC</Label><Input value={form.bank_ifsc || ""} onChange={set("bank_ifsc")} /></div>
          <div><Label>Branch</Label><Input value={form.bank_branch || ""} onChange={set("bank_branch")} /></div>
          <div><Label>UPI ID</Label><Input value={form.upi_id || ""} onChange={set("upi_id")} placeholder="name@bank" /></div>
          <div><Label>UPI Payee Name</Label><Input value={form.upi_name || ""} onChange={set("upi_name")} /></div>

          <div className="md:col-span-2">
            <Label>Payment QR (image)</Label>
            <div className="flex items-center gap-2 mt-1">
              {form.qr_url ? <img src={form.qr_url} alt="qr" className="h-14 w-14 object-contain border rounded" /> : <div className="h-14 w-14 border rounded flex items-center justify-center text-[10px] text-slate-400">No QR</div>}
              <input type="file" accept="image/*" onChange={(e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const rd = new FileReader(); rd.onload = () => setForm(v => ({ ...v, qr_url: rd.result })); rd.readAsDataURL(f);
              }} className="text-xs" />
              {form.qr_url && <Button variant="outline" size="sm" onClick={() => setForm(v => ({ ...v, qr_url: "" }))}>Remove</Button>}
            </div>
          </div>

          <div className="md:col-span-2">
            <LocationDocumentsBlock
              lat={form.gps_lat} lng={form.gps_lng} locationLink={form.location_link}
              onLat={(v) => setForm(f => ({ ...f, gps_lat: v }))}
              onLng={(v) => setForm(f => ({ ...f, gps_lng: v }))}
              onLocationLink={(v) => setForm(f => ({ ...f, location_link: v }))}
              documents={form.documents || []}
              onDocuments={(docs) => setForm(f => ({ ...f, documents: docs }))}
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="myprofile-save">
            {saving ? "Saving…" : "Save Profile"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
